/**
 * Maximum number of values allowed in a single multi-value filter instance
 * (e.g. a pasted list of serial numbers or asset IDs in one filter chip).
 *
 * Product-agreed ceiling on EDUS-2575: customers cite ~2,000 typical bulk
 * pastes; 3,000 covers the ceiling plus headroom. Enforced at the **matcher**
 * level on the frontend (so every field using `multiContains` /
 * `multiTextSearch` inherits it) and re-validated on the backend (request
 * normalization + filter-snapshot create). The cap is per multi-value filter
 * instance; a request may carry several such filters.
 *
 * Counted against the **raw** value-array length the user pasted/added, before
 * any dedup — so the message ("Maximum 3,000 values per filter") matches what
 * was pasted.
 */
export const MAX_MULTI_VALUE_FILTER_VALUES = 3000;

/**
 * Matcher names whose `matchValue` is a list of values subject to
 * {@link MAX_MULTI_VALUE_FILTER_VALUES}. Kept here so front and back agree on
 * exactly which matchers are "multi-value".
 */
export const MULTI_VALUE_MATCHER_NAMES = ['multiContains', 'multiTextSearch'] as const;

export type MultiValueMatcherName = (typeof MULTI_VALUE_MATCHER_NAMES)[number];

/**
 * Extracts the value array from a multi-value matcher's `matchValue`, or returns
 * `null` if the matcher isn't multi-value / the value can't be parsed.
 *
 * - `multiContains` → `matchValue` is a JSON-stringified `string[]`.
 * - `multiTextSearch` → `matchValue` is a JSON-stringified
 *   `{ mode: 'contains' | 'startsWith', values: string[] }`.
 *
 * Lives with the cap so the front/back cap checks count values identically.
 */
export function getMultiValueFilterValues(
  matcherName: string | undefined,
  matchValue: unknown
): string[] | null {
  const isMultiValue =
    !!matcherName &&
    MULTI_VALUE_MATCHER_NAMES.includes(matcherName as MultiValueMatcherName);
  if (!isMultiValue) {
    return null;
  }
  if (typeof matchValue !== 'string') {
    return Array.isArray(matchValue) ? (matchValue as string[]) : null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(matchValue);
  } catch {
    return null;
  }
  if (Array.isArray(parsed)) {
    return parsed as string[];
  }
  if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { values?: unknown }).values)) {
    return (parsed as { values: string[] }).values;
  }
  return null;
}

/**
 * Returns the first multi-value filter whose raw value count exceeds
 * {@link MAX_MULTI_VALUE_FILTER_VALUES}, or `null` if all are within the cap.
 * Pure + framework-agnostic so the NestJS boundary can map a violation to a
 * `400` (request normalization + filter-snapshot create), and the matcher
 * editors can pre-empt it on the client.
 */
export function findMultiValueFilterCapViolation(
  activeFilters: ReadonlyArray<{ selectedMatcher?: { name?: string }; matchValue?: unknown }>
): { fieldMatcher: string; count: number; cap: number } | null {
  for (const filter of activeFilters ?? []) {
    const values = getMultiValueFilterValues(filter.selectedMatcher?.name, filter.matchValue);
    if (values && values.length > MAX_MULTI_VALUE_FILTER_VALUES) {
      return {
        fieldMatcher: filter.selectedMatcher?.name ?? 'unknown',
        count: values.length,
        cap: MAX_MULTI_VALUE_FILTER_VALUES,
      };
    }
  }
  return null;
}

/** Human-facing cap message; shared so the snackbar and the API error match. */
export const MULTI_VALUE_FILTER_CAP_MESSAGE = `Maximum ${MAX_MULTI_VALUE_FILTER_VALUES.toLocaleString()} values per filter`;

/**
 * Escapes SQL `LIKE`/`ILIKE` wildcards (`%`, `_`) and the escape character
 * itself (`\`) so user-supplied filter values are matched literally.
 *
 * Without this, a pasted asset ID like `ROOM_A_DEVICE_1` matches
 * `ROOM-A-DEVICE-1` via the single-character `_` wildcard.
 *
 * Lives beside {@link MAX_MULTI_VALUE_FILTER_VALUES} because both guard the same
 * paste-mode validation codepath. The sole caller today is the paste-mode
 * validation endpoint (`DevicesService.validateFilterValues`), which uses this
 * to classify pasted values before they hit the grid.
 *
 * The row-filter/grid query path (the `multiTextSearch` ILike pattern built in
 * `typeorm.data-repo.ts`) does NOT call this function and performs no
 * equivalent escaping, so a value containing `_` or `%` can classify
 * differently in the validation summary than it filters in the grid.
 *
 * Backslashes are escaped first so an input `\` can't smuggle in an escape
 * sequence that neutralizes the following character.
 */
export function escapeLikeWildcards(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/[%_]/g, '\\$&');
}
